import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Checkbox from "components/checkbox";
import { FiMail, FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import { Link } from "react-router-dom";
import AuthService from "../../service/auth.service";
import { Spinner } from "../../components/loaders/Spinner.js";
import InputWithIcon from "../../components/input/InputWithIcon";

export default function SignIn() {
  const [email_user, setEmailUser] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = new URLSearchParams(location.search).get("redirect");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email_user.trim() || !password.trim()) {
      setError("Veuillez remplir tous les champs");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await AuthService.login({ email_user, password });
      navigate(redirect || "/admin/default", { replace: true });
    } catch (error) {
      console.error("Login error:", error);
      setError(
        error.response?.data?.message ||
          "Échec de la connexion. Veuillez vérifier vos identifiants."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 to-gray-50 dark:from-navy-800 dark:to-navy-900">
      {/* Left Side - Image/Logo */}
      <div className="hidden w-1/2 items-center justify-center bg-gradient-to-br from-[#57A863] to-[#2E5A33] lg:flex lg:w-1/2">
        <div className="max-w-md text-center text-white">
          <h1 className="mb-8 text-4xl font-bold">ATLAS ADMIN</h1>
          <p className="text-lg">Système de gestion professionnelle</p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2">
        <div className="flex min-h-screen items-center justify-center px-6 py-12 lg:px-8">
          <div className="w-full max-w-md space-y-8">
            {/* Header */}
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Bienvenue sur ATLAS
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Connectez-vous à votre espace de travail
              </p>
            </div>

            {/* Form */}
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Email Input */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-400">
                    Adresse Email
                  </label>
                  <InputWithIcon
                    icon={FiMail}
                    placeholder="exemple@atlas.com"
                    id="email_user"
                    type="email"
                    value={email_user}
                    onChange={(e) => setEmailUser(e.target.value)}
                    disabled={loading}
                    required
                    className="focus:border-[#57A863] focus:ring-[#57A863]"
                  />
                </div>

                {/* Password Input with show/hide icon */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-400">
                    Mot de passe
                  </label>
                  <div className="relative">
                    <InputWithIcon
                      icon={FiLock}
                      placeholder="••••••••"
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      required
                      className="pr-10 focus:border-[#57A863] focus:ring-[#57A863]"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-white"
                      onClick={() => setShowPassword((prev) => !prev)}
                      disabled={loading}
                      aria-label={
                        showPassword
                          ? "Cacher le mot de passe"
                          : "Afficher le mot de passe"
                      }
                    >
                      {showPassword ? (
                        <FiEyeOff size={20} />
                      ) : (
                        <FiEye size={20} />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                  />
                  <label
                    className={`ml-2 text-sm ${
                      loading
                        ? "text-gray-400"
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    Se souvenir de moi
                  </label>
                </div>
                <div className="text-sm">
                  <Link
                    to="/"
                    className={`font-medium transition-colors ${
                      loading
                        ? "pointer-events-none text-[#57A863]/50"
                        : "text-[#57A863] hover:text-[#2E5A33]"
                    }`}
                    tabIndex={loading ? -1 : 0}
                  >
                    Mot de passe oublié?
                  </Link>
                </div>
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={loading}
                className="group relative flex h-12 w-full items-center justify-center rounded-lg bg-gradient-to-r from-[#57A863] to-[#2E5A33] px-4 py-3 text-sm font-semibold text-white transition-all hover:from-[#2E5A33] hover:to-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 disabled:opacity-70"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <Spinner size="sm" className="mr-2" />
                    <span>Connexion en cours...</span>
                  </div>
                ) : (
                  "Se connecter"
                )}
              </button>

              {/* Error Message */}
              {error && (
                <div className="mt-4 rounded-md bg-red-50 p-3 text-center text-sm text-red-600">
                  {error}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
